import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from './components/Sidebar';
import TopNavbar from './components/TopNavbar';
import DataContent from './components/DataContent';
import UploadContent from './components/UploadContent/index';
import PdfUploadContent from './components/PdfUploadContent';
import ViewPdfContent from './components/ViewPdfContent';
import DashboardContent from './components/DashboardContent';
import HomeContent from './components/HomeContent';
import UserAccessContent from './components/UserAccessContent';
import CompaniesContent from './components/CompaniesContent';
import Footer from './components/Footer';

const Dashboard = ({ userInfo, setIsAuthenticated }) => {
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activeSection, setActiveSection] = useState('home');
  const [searchQuery, setSearchQuery] = useState('');
  const [extractedData, setExtractedData] = useState(null);
  const [fullscreenMode, setFullscreenMode] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [showBackArrow, setShowBackArrow] = useState(false);
  const [activeStatement, setActiveStatement] = useState('Income Statement');
  
  // Excel upload/download state
  const [uploadedExcelData, setUploadedExcelData] = useState(null);
  const [triggerDownload, setTriggerDownload] = useState(false);
  
  // Financial data state for passing to TopNavbar
  const [financialData, setFinancialData] = useState(null);
  
  // UPDATED: Remove extraction flow tracking states
  // We no longer need to track processing state for redirection

  const [navigationHistory, setNavigationHistory] = useState([]);

  // Reset states when switching sections
  useEffect(() => {
    if (activeSection === 'home') {
      setFullscreenMode(false);
      setSidebarOpen(true);
      setSelectedCompany(null);
      setShowBackArrow(false);
      setNavigationHistory([]);
      setExtractedData(null);
      setUploadedExcelData(null);
      setFinancialData(null);
    } else if (activeSection === 'data') {
      setFullscreenMode(true);
      setSidebarOpen(false);
      
      // Show back arrow if we came from upload section
      const lastNav = navigationHistory[navigationHistory.length - 1];
      const shouldShowBack = lastNav === 'upload';
      setShowBackArrow(shouldShowBack);
    } else if (activeSection === 'companies' || activeSection === 'upload-pdf' || activeSection === 'view-pdf' || activeSection === 'user-access') {
      // Handle companies, upload-pdf, view-pdf, and user-access sections - keep sidebar open, not fullscreen
      setFullscreenMode(false);
      setSidebarOpen(true);
      setShowBackArrow(false);
      // Clear extracted data when switching to companies section
      if (activeSection === 'companies') {
        setExtractedData(null);
        setUploadedExcelData(null);
        setFinancialData(null);
      }
    } else if (activeSection === 'upload') {
      // Upload section should not be fullscreen anymore
      setFullscreenMode(false);
      setSidebarOpen(true);
      setShowBackArrow(false);
    } else {
      setFullscreenMode(false);
      setSidebarOpen(true);
      setShowBackArrow(false);
      setExtractedData(null);
      setUploadedExcelData(null);
      setFinancialData(null);
    }
  }, [activeSection, navigationHistory]);

  const handleLogout = () => {
    localStorage.removeItem('currentUser');
    localStorage.removeItem('access_token');
    setIsAuthenticated(false);
    navigate('/login', { replace: true });
  };

  // UPDATED: Simplified extraction complete handler
  const handleExtractionComplete = (extractedData, extractionFiles, sections) => {
    console.log('Upload completed successfully');
    
    // Note: The success message is already shown in the UploadContent component
    // We don't need to redirect or set any states here
    
    // Just log for debugging
    if (extractedData && extractedData.success) {
      console.log('Job completed:', extractedData.jobId);
    }
    
    // Stay in upload section - no redirection
  };

  const handleHomeClick = () => {
    setActiveSection('home');
    setFullscreenMode(false);
    setSidebarOpen(true);
    setSelectedCompany(null);
    setShowBackArrow(false);
    setNavigationHistory([]);
    setExtractedData(null);
    setUploadedExcelData(null);
    setFinancialData(null);
  };

  const handleCompanySelect = (company) => {
    setSelectedCompany(company);
    setNavigationHistory(prev => [...prev, 'search']);
    setActiveSection('data');
    setSearchQuery(`${company.name} (${company.ticker})`);
    setShowBackArrow(false);
    setExtractedData(null);
    setUploadedExcelData(null);
    setFinancialData(null);
  };

  const handleDataClick = () => {
    // Reset search query when coming directly from sidebar
    setSearchQuery('');
    
    // Reset selected company when coming directly from sidebar
    setSelectedCompany(null);
    
    // Clear extracted data when coming from sidebar
    setExtractedData(null);
    
    // Clear uploaded Excel data
    setUploadedExcelData(null);
    
    // Clear financial data
    setFinancialData(null);
    
    // Set activeSection to 'data'
    setActiveSection('data');
    
    setFullscreenMode(true);
    setSidebarOpen(false);
    setShowBackArrow(false);
    
    // Clear navigation history
    setNavigationHistory([]);
  };

  const handleBackFromData = () => {
    const lastNav = navigationHistory[navigationHistory.length - 1];
    
    if (lastNav === 'upload') {
      // If coming from upload, go back to upload
      setActiveSection('upload');
      setFullscreenMode(false);
      setSidebarOpen(true);
      setShowBackArrow(false);
    } else if (selectedCompany) {
      // If we have a selected company, go to home
      setActiveSection('home');
      setFullscreenMode(false);
      setSidebarOpen(true);
      setShowBackArrow(false);
      setExtractedData(null);
      setUploadedExcelData(null);
      setFinancialData(null);
    } else {
      // If no company selected (fresh data screen), go to home
      setActiveSection('home');
      setFullscreenMode(false);
      setSidebarOpen(true);
      setShowBackArrow(false);
      setExtractedData(null);
      setUploadedExcelData(null);
      setFinancialData(null);
    }
  };

  // Handle Excel upload from TopNavbar
  const handleExcelUpload = (uploadedData, statementType) => {
    console.log('Excel upload received:', uploadedData, statementType);
    
    setUploadedExcelData({
      data: uploadedData,
      statementType: statementType || 'Income Statement'
    });
    
    // Clear other data sources
    setExtractedData(null);
    
    // Set selected company to null (will be set in DataContent)
    setSelectedCompany(null);
    setFinancialData(null);
    
    // Navigate to data section
    setActiveSection('data');
    setFullscreenMode(true);
    setSidebarOpen(false);
    setShowBackArrow(false);
    
    // Clear search query
    setSearchQuery('');
    
    // Clear navigation history
    setNavigationHistory([]);
  };

  // Handle Excel download from TopNavbar
  const handleExcelDownload = () => {
    console.log('Download triggered from TopNavbar');
    setTriggerDownload(true);
    
    // Reset after a short delay
    setTimeout(() => {
      setTriggerDownload(false);
    }, 100);
  };

  // Handle download completion from DataContent
  const handleDownloadComplete = (success, fileName) => {
    if (success) {
      console.log(`Download completed: ${fileName}`);
    } else {
      console.error('Download failed');
    }
  };

  // Handle section change from sidebar
  const handleSectionChange = (section) => {
    setActiveSection(section);
    
    // Reset certain states when switching sections
    if (section === 'companies' || section === 'view-pdf' || section === 'user-access') {
      setSearchQuery('');
      setSelectedCompany(null);
      setExtractedData(null);
      setUploadedExcelData(null);
      setFinancialData(null);
    } else if (section === 'upload') {
      // Upload section should not be fullscreen
      setFullscreenMode(false);
      setSidebarOpen(true);
      setShowBackArrow(false);
    } else if (section === 'home') {
      // Reset everything when going to home
    }
  };

  // Handle financial data update from DataContent
  const handleFinancialDataUpdate = (data) => {
    setFinancialData(data);
  };

  const renderContent = () => {
    switch (activeSection) {
      case 'home':
        return <HomeContent />;
      case 'data':
        return (
          <DataContent 
            searchQuery={searchQuery} 
            setSearchQuery={setSearchQuery}
            extractedData={extractedData}
            fullscreenMode={fullscreenMode}
            selectedCompany={selectedCompany}
            showBackArrow={showBackArrow}
            onBackClick={handleBackFromData}
            uploadedExcelData={uploadedExcelData}
            setUploadedExcelData={setUploadedExcelData}
            activeStatement={activeStatement}
            onExcelDownload={triggerDownload ? 'trigger' : null}
            onDownloadComplete={handleDownloadComplete}
            onFinancialDataUpdate={handleFinancialDataUpdate}
          />
        );
      case 'upload':
        // Always show UploadContent (no special processing state)
        return <UploadContent onExtractionComplete={handleExtractionComplete} />;
      case 'upload-pdf':
        return <PdfUploadContent />;
      case 'view-pdf':
        return <ViewPdfContent />;
      case 'dashboard':
        return <DashboardContent />;
      case 'user-access':
        return <UserAccessContent />;
      case 'companies':
        return <CompaniesContent />;
      default:
        return <HomeContent />;
    }
  };

  // Determine if we need scrollable content area
  const needsScrollableContent = () => {
    return ['companies', 'upload-pdf', 'view-pdf', 'user-access'].includes(activeSection);
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col">
      {/* Fixed Top Navigation */}
      <div className="fixed top-0 left-0 right-0 z-40">
        <TopNavbar 
          sidebarOpen={sidebarOpen}
          setSidebarOpen={setSidebarOpen}
          activeSection={activeSection}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          handleLogout={handleLogout}
          onCompanySelect={handleCompanySelect}
          fullscreenMode={fullscreenMode}
          onHomeClick={handleHomeClick}
          onDataClick={handleDataClick}
          showBackArrow={showBackArrow && activeSection === 'data'}
          onBackClick={handleBackFromData}
          userRole={userInfo?.role || 'admin'}
          onExcelUpload={handleExcelUpload}
          onExcelDownload={handleExcelDownload}
          activeStatement={activeStatement}
          currentCompany={selectedCompany}
          financialData={financialData}
        />
      </div>
      
      {/* Sidebar and Content Container */}
      <div className="flex flex-1 pt-16">
        <Sidebar 
          sidebarOpen={sidebarOpen}
          setSidebarOpen={setSidebarOpen}
          activeSection={activeSection}
          setActiveSection={handleSectionChange}
          userInfo={userInfo}
          fullscreenMode={fullscreenMode}
          onDataClick={handleDataClick}
        />
        
        <div className={`flex-1 flex flex-col transition-all duration-300 ${fullscreenMode ? 'ml-0' : sidebarOpen ? 'ml-64' : 'ml-16'}`}>
          <main className={`flex-1 ${fullscreenMode ? 'p-0' : 'p-6'} ${needsScrollableContent() ? 'overflow-y-auto h-[calc(100vh-140px)]' : ''}`}>
            {renderContent()}
          </main>
          
          {/* Footer - Only show when not in fullscreen mode */}
          {!fullscreenMode && <Footer />}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;